import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import React, { Suspense } from "react";
import { ApiInternal } from "src/api/api_internal";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { Subheading } from "src/components/base/heading";
import { Button, PrimaryButton } from "src/components/base/button";
import { GlobeAltIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { Text } from "src/components/base/text";
import TenantAddDomainDialog from "src/components/dialogs/tenant-add-domain";
import { EmptyState } from "src/components/base/empty-state";

/**
 * The properties for {@link SettingDns}
 */
export type SettingDnsProps = {};

/**
 * Setting page to configure tenant specific DNS
 */
function SettingDns(props: SettingDnsProps) {
    const [t] = useTranslation("tenant-dns");
    const [tg] = useTranslation();

    const router = useRouter();
    const { allDomains, tenantDomains } = Route.useLoaderData();
    const { tenantId } = Route.useParams();

    const [openAdd, setOpenAdd] = React.useState(false);

    const unassigned = allDomains.filter((domain) => domain.tenant == null);

    return (
        <div className={"flex flex-col gap-5"}>
            <div className={"flex items-center justify-between"}>
                <div className={"flex flex-col gap-1"}>
                    <Subheading>{t("heading.split-domains")}</Subheading>
                    <Text>{t("description.split-domains")}</Text>
                </div>
                <PrimaryButton onClick={() => setOpenAdd(true)}>{t("button.add-split-domain")}</PrimaryButton>
            </div>

            {tenantDomains.length === 0 ? (
                <EmptyState
                    icon={<GlobeAltIcon />}
                    title={t("empty.title")}
                    description={t("empty.description")}
                    action={
                        <Button color={"blue"} onClick={() => setOpenAdd(true)}>
                            <PlusIcon />
                            {t("button.add-split-domain")}
                        </Button>
                    }
                />
            ) : (
                <Table dense={true} className={"mt-3"}>
                    <TableHead>
                        <TableRow>
                            <TableHeader>{t("label.domain")}</TableHeader>
                            <TableHeader>
                                <span className={"sr-only"}>{tg("label.actions")}</span>
                            </TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tenantDomains.map((domain) => (
                            <TableRow key={domain.uuid}>
                                <TableCell>{domain.domain}</TableCell>
                                <TableCell className={"text-right"}>
                                    <Button
                                        plain={true}
                                        onClick={async () => {
                                            await ApiInternal.tenantsDns.removeDomain(tenantId, domain.uuid);
                                            router.invalidate({ sync: true });
                                        }}
                                    >
                                        <TrashIcon />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Suspense>
                <TenantAddDomainDialog
                    isOpen={openAdd}
                    onClose={() => setOpenAdd(false)}
                    tenantId={tenantId}
                    domains={unassigned}
                    onSubmit={async () => {
                        setOpenAdd(false);
                        await router.invalidate({ sync: true });
                    }}
                />
            </Suspense>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/tenants/$tenantId/_tenant/split-dns")({
    component: SettingDns,
    loader: async ({ params }) => {
        const [{ list: allDomains }, { list: tenantDomains }] = await Promise.all([
            ApiInternal.dns.getAllSplitDns(),
            ApiInternal.tenantsDns.getAll(params.tenantId),
        ]);
        return { allDomains, tenantDomains };
    },
});
